import * as XLSX from "xlsx";
import { TableSegment } from "../segmentationTableUtils";
import { DriverTableSegment } from "../driverTableUtils";
import { HeadroomTableSegment } from "../headroomTableUtils";

export interface Cell {
  v: string | number;
  s?: {
    font?: { bold?: boolean };
    alignment?: { horizontal?: string };
  };
}

/**
 * Formats the current date and time into a string suitable for filenames.
 * Format: YYYY-MM-DD_HH-MM-[AM/PM]_EEST
 * @returns {string} Formatted datetime string
 */
const getFormattedDateTimeForFilename = (): string => {
  const now = new Date();
  return `${now.getFullYear()}-${(now.getMonth() + 1).toString().padStart(2, "0")}-${now
    .getDate()
    .toString()
    .padStart(2, "0")}_${now.getHours() > 12 ? now.getHours() - 12 : now.getHours()}-${now
    .getMinutes()
    .toString()
    .padStart(2, "0")}-${now.getHours() >= 12 ? "PM" : "AM"}_EEST`;
};

/**
 * Writes the worksheet and exports it to an Excel file.
 * @param worksheetData - Array of worksheet data (headers and rows).
 * @param segmentsToExport - Array of table segments to export.
 * @param segmentColumns - Number of segment columns.
 * @param driver1StartColumn - Starting column index for driver 1 metrics.
 * @param driver2StartColumn - Starting column index for driver 2 metrics.
 * @param tableName - Base name for the exported Excel file.
 */
export const writeWorksheet = (
  worksheetData: (string | number | Cell)[][],
  segmentsToExport: TableSegment[],
  segmentColumns: number,
  driver1StartColumn: number,
  driver2StartColumn: number,
  tableName: string
): void => {
  const worksheet: XLSX.WorkSheet = XLSX.utils.aoa_to_sheet(worksheetData);

  const merges: XLSX.Range[] = worksheet["!merges"] || [];
  merges.push(
    { s: { r: 0, c: 0 }, e: { r: 0, c: segmentColumns - 1 } },
    { s: { r: 0, c: driver1StartColumn }, e: { r: 0, c: driver1StartColumn + 4 } },
    { s: { r: 0, c: driver2StartColumn }, e: { r: 0, c: driver2StartColumn + 4 } }
  );

  const hasOneDataAndOneManualSegment =
    segmentsToExport.length === 2 &&
    segmentsToExport.some((seg) => seg.method === "DATA") &&
    segmentsToExport.some((seg) => seg.method === "MANUAL");
  const isManualOnly = segmentsToExport.length === 1 && segmentsToExport.every((seg) => seg.method === "MANUAL");
  const isTwoSameMethod =
    segmentsToExport.length === 2 && segmentsToExport.every((seg) => seg.method === segmentsToExport[0].method);

  if (!hasOneDataAndOneManualSegment && !isManualOnly && !isTwoSameMethod) {
    segmentsToExport.forEach((_: TableSegment, index: number) => {
      merges.push({
        s: { r: 1, c: index * 2 },
        e: { r: 1, c: index * 2 + 1 },
      });
    });
  }

  worksheet["!merges"] = merges;

  worksheet["!cols"] = Array(segmentColumns + 13).fill({ wch: 15 });

  const workbook: XLSX.WorkBook = XLSX.utils.book_new();
  XLSX.utils.book_append_sheet(workbook, worksheet, "Segmentation Data");
  XLSX.writeFile(workbook, `${tableName}_Segmentation_Export_${getFormattedDateTimeForFilename()}.xlsx`);
};

/**
 * Writes the driver metrics data to an Excel worksheet and saves it.
 * @param data - The worksheet data (headers + rows).
 * @param segments - Array of driver table segments.
 * @param segmentColumns - Number of segment columns.
 * @param driverStartColumn - Starting column index for driver metrics.
 * @param tableName - Name of the worksheet and file.
 */
export const writeDriverWorksheet = (
  data: (string | number | Cell)[][],
  segments: DriverTableSegment[],
  segmentColumns: number,
  driverStartColumn: number,
  tableName: string
): void => {
  const worksheet: XLSX.WorkSheet = XLSX.utils.aoa_to_sheet(data);

  const merges: XLSX.Range[] = worksheet["!merges"] || [];
  merges.push(
    { s: { r: 0, c: 0 }, e: { r: 0, c: segmentColumns - 1 } },
    { s: { r: 0, c: driverStartColumn }, e: { r: 0, c: driverStartColumn + 1 } }
  );

  const hasOneDataAndOneManualSegment =
    segments.length === 2 &&
    segments.some((seg) => seg.method === "DATA") &&
    segments.some((seg) => seg.method === "MANUAL");
  const isManualOnly = segments.length === 1 && segments.every((seg) => seg.method === "MANUAL");
  const isTwoSameMethod = segments.length === 2 && segments.every((seg) => seg.method === segments[0].method);

  if (!hasOneDataAndOneManualSegment && !isManualOnly && !isTwoSameMethod) {
    segments.forEach((_: DriverTableSegment, index: number) => {
      merges.push({
        s: { r: 1, c: index * 2 },
        e: { r: 1, c: index * 2 + 1 },
      });
    });
  }

  worksheet["!merges"] = merges;

  const colWidths = Array(segmentColumns + 2).fill({ wch: 15 });
  if (segments.length === 1 || segments.some((seg) => seg.method === "MANUAL")) {
    colWidths[0] = { wch: 15 };
    colWidths[1] = { wch: 25 };
  } else {
    segments.forEach((_, index) => {
      colWidths[index * 2] = { wch: 25 };
      colWidths[index * 2 + 1] = { wch: 25 };
    });
  }
  colWidths[driverStartColumn] = { wch: 20 };
  colWidths[driverStartColumn + 1] = { wch: 20 };
  worksheet["!cols"] = colWidths;

  const range = XLSX.utils.decode_range(worksheet["!ref"] || "A1");
  for (let col = range.s.c; col <= range.e.c; col++) {
    const cellAddress1 = XLSX.utils.encode_cell({ r: 0, c: col });
    const cellAddress2 = XLSX.utils.encode_cell({ r: 1, c: col });
    if (worksheet[cellAddress1] && typeof worksheet[cellAddress1].v === "string") {
      worksheet[cellAddress1].s = { font: { bold: true }, alignment: { horizontal: "center" } };
    }
    if (worksheet[cellAddress2] && typeof worksheet[cellAddress2].v === "string") {
      worksheet[cellAddress2].s = { font: { bold: true }, alignment: { horizontal: "center" } };
    }
  }

  const workbook: XLSX.WorkBook = XLSX.utils.book_new();
  XLSX.utils.book_append_sheet(workbook, worksheet, tableName || "Driver Metrics");
  XLSX.writeFile(workbook, `${tableName}_DriverMetrics_Export_${getFormattedDateTimeForFilename()}.xlsx`);
};

/**
 * Writes the headroom final data to an Excel worksheet and saves it.
 */
export const writeHeadroomFinalWorksheet = (
  data: (string | number | Cell)[][],
  segments: HeadroomTableSegment[],
  segmentColumns: number,
  driverStartColumn: number,
  tableName: string
): void => {
  const worksheet: XLSX.WorkSheet = XLSX.utils.aoa_to_sheet(data);

  const merges: XLSX.Range[] = worksheet["!merges"] || [];
  merges.push(
    { s: { r: 0, c: 0 }, e: { r: 0, c: segmentColumns - 1 } },
    { s: { r: 0, c: driverStartColumn }, e: { r: 0, c: driverStartColumn + 2 } }
  );

  const hasOneDataAndOneManualSegment =
    segments.length === 2 &&
    segments.some((seg) => seg.method === "DATA") &&
    segments.some((seg) => seg.method === "MANUAL");
  const isManualOnly = segments.length === 1 && segments.every((seg) => seg.method === "MANUAL");
  const isTwoSameMethod = segments.length === 2 && segments.every((seg) => seg.method === segments[0].method);

  if (!hasOneDataAndOneManualSegment && !isManualOnly && !isTwoSameMethod) {
    segments.forEach((_, index: number) => {
      merges.push({
        s: { r: 1, c: index * 2 },
        e: { r: 1, c: index * 2 + 1 },
      });
    });
  }

  worksheet["!merges"] = merges;

  const colWidths = Array(segmentColumns + 11).fill({ wch: 15 });
  if (segments.length === 1 || hasOneDataAndOneManualSegment) {
    colWidths[0] = { wch: 25 };
    colWidths[1] = { wch: 25 };
  } else if (isTwoSameMethod) {
    colWidths[0] = { wch: 25 };
    colWidths[1] = { wch: 25 };
  } else {
    segments.forEach((_, index) => {
      colWidths[index * 2] = { wch: 25 };
      colWidths[index * 2 + 1] = { wch: 25 };
    });
  }
  for (let i = driverStartColumn; i < driverStartColumn + 11; i++) {
    colWidths[i] = { wch: 20 };
  }
  worksheet["!cols"] = colWidths;

  const range = XLSX.utils.decode_range(worksheet["!ref"] || "A1");
  for (let col = range.s.c; col <= range.e.c; col++) {
    const cellAddress1 = XLSX.utils.encode_cell({ r: 0, c: col });
    const cellAddress2 = XLSX.utils.encode_cell({ r: 1, c: col });
    if (worksheet[cellAddress1] && typeof worksheet[cellAddress1].v === "string") {
      worksheet[cellAddress1].s = { font: { bold: true }, alignment: { horizontal: "center" } };
    }
    if (worksheet[cellAddress2] && typeof worksheet[cellAddress2].v === "string") {
      worksheet[cellAddress2].s = { font: { bold: true }, alignment: { horizontal: "center" } };
    }
  }

  const workbook: XLSX.WorkBook = XLSX.utils.book_new();
  XLSX.utils.book_append_sheet(workbook, worksheet, tableName || "Headroom Final Results");
  XLSX.writeFile(workbook, `${tableName}_HeadroomFinal_Export_${getFormattedDateTimeForFilename()}.xlsx`);
};
